import { ModuleFlowExecutor } from "by/gamefactory/cityversetycoon/composition/module/ModuleFlowExecutor";
import { MarketplaceModule } from "./MarketplaceModule";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { MarketplaceScene, MarketplaceSceneOptions } from "./main/MarketplaceScene";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IMarketplaceMapModel } from "./main/models/map/IMarketplaceMapModel";
import { IMarketplaceNewsModel } from "./main/models/news/IMarketplaceNewsModel";
import { IBenefitsModel } from "../../common/models/benefits/IBenefitsModel";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { nameof } from "bindable-data";
import { CancellationTokenSource } from "async/CancellationTokenSource";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { TopPanel } from "../../game/components/toppanel/TopPanel";

type MarketplaceSceneFactory = (options: MarketplaceSceneOptions) => Promise<MarketplaceScene>;

@injectable()
export class MarketplaceModuleFlowExecutor extends ModuleFlowExecutor<MarketplaceModule> {

    private readonly sceneManager: SceneManager;
    private readonly connectionModel: IConnectionModel;
    private readonly mapModel: IMarketplaceMapModel;
    private readonly newsModel: IMarketplaceNewsModel;
    private readonly benefitsModel: IBenefitsModel;
    private readonly playerModel: IPlayerModel;
    private readonly topPanel: TopPanel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    private readonly cancellationTokenSource: CancellationTokenSource;

    private topPanelOnNotificationAfterExecute: boolean;

    public constructor(
        sceneManager: SceneManager,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("IMarketplaceMapModel") mapModel: IMarketplaceMapModel,
        @inject("IMarketplaceNewsModel") newsModel: IMarketplaceNewsModel,
        @inject("IBenefitsModel") benefitsModel: IBenefitsModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        topPanel: TopPanel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.sceneManager = sceneManager;
        this.connectionModel = connectionModel;
        this.mapModel = mapModel;
        this.newsModel = newsModel;
        this.benefitsModel = benefitsModel;
        this.playerModel = playerModel;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.cancellationTokenSource = new CancellationTokenSource();
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChanged, this);
        this.topPanel.onLayerChanged.add(this.onTopPanelLayerChanged, this);
    }

    public async execute(module: MarketplaceModule, buildingArea?: number): Promise<void> {
        try {
            if (buildingArea == null) {
                throw new Error("Unable to executre MarketplaceModule withour buildingArea");
            }

            this.asyncOperationNotification.show();
            const cancellationToken = this.cancellationTokenSource.token;
            const options: MarketplaceSceneOptions = {
                buildingArea: buildingArea,
                cancellationToken: cancellationToken
            };

            await this.playerModel.loadBuildings(false);
            await this.mapModel.updateAreas();

            if (cancellationToken.isCancellationRequested) {
                return;
            }

            await Promise.all([this.newsModel.loadMarketplaceNews(), this.benefitsModel.getBenefits()]);
            if (cancellationToken.isCancellationRequested) {
                return;
            }

            const factory: MarketplaceSceneFactory = await module.resolve("MarketplaceSceneFactory");
            if (cancellationToken.isCancellationRequested) {
                return;
            }

            const scene = await factory(options);
            if (cancellationToken.isCancellationRequested) {
                scene.view?.destroy();
                return;
            }

            this.asyncOperationNotification.hide();
            this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToOriginParent(true);
            }
            await this.sceneManager.showViewableAsync(scene);
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            if (this.topPanelOnNotificationAfterExecute) {
                this.topPanel.bringToFront();
            }
            else {
                this.topPanel.bringToOriginParent();
            }
        }
        catch (e: unknown) {
            this.logger.error(e);
        }
        finally {
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            this.cancellationTokenSource.dispose();
            this.connectionModel.propertyChanged.remove(this.onConnectionModelPropertyChanged, this);
            this.asyncOperationNotification.hide();
        }
    }

    private onConnectionModelPropertyChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug("MarketplaceModule cancellation requested, reason: disconnect");
                    this.cancellationTokenSource.cancel();
                }
                break;
        }
    }

    private onTopPanelLayerChanged(): void {
        this.topPanelOnNotificationAfterExecute = this.topPanel.onNotificationLayer;
        if (this.topPanel.onNotificationLayer) {
            this.topPanel.bringToOriginParent(true);
        }
    }
}
